import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Stack } from 'new/elements/Stack';
import { Info } from 'new/components/monitor/components/Info';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import useResourceCount from 'new/hooks/adapters/useResourceCount';
import { fetchAccountSummaryByOrg } from 'js/actions';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_RESOURCE_COUNT_REQUEST } from 'js/constants/actionTypes';
import FormatUtils from 'js/lib/FormatUtils';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { ReactComponent as RefreshIconRotate } from 'new/components/icons/fi-rr-refresh-rotate.svg';
import LightningStatus from 'new/elements/LightningStatus';
import Constants from 'js/Constants';
import { Switch } from 'new/elements/Switch';
import useAppDispatch from 'js/hooks/useAppDispatch';

import styles from './ResourceCount.module.scss';

interface ResourceCountProps {
  rootClassName?: string;
  className?: string;
  detailed?: boolean;
  showShortTitle?: boolean;
  omitHeaderTextInfo?: boolean;
  controlled?: boolean;
  externalIsOrgView?: boolean;
  externalOnControlChange?: () => void;
}

type RightViewProps = {
  admin?: boolean;
  onChange?: (value: any) => void;
  onRefresh: (e: SyntheticEvent) => void;
  isLoading: boolean;
  isOrgView: boolean;
  omitSwitch?: boolean;
};

const RightView = (props: RightViewProps) => {
  const { onRefresh, isLoading, isOrgView, admin, onChange, omitSwitch = false } = props;

  if (!admin) {
    return (
      <Stack flex={1} direction="row" justifyContent="space-between" alignItems="center">
        {isLoading ? (
          <RefreshIconRotate fontSize="large" className="refreshIcon" />
        ) : (
          <RefreshIcon onClick={onRefresh} fontSize="large" className="refreshIcon" />
        )}
      </Stack>
    );
  }

  return (
    <Stack flex={1} direction="row" justifyContent="space-between" alignItems="center">
      {isLoading ? (
        <RefreshIconRotate fontSize="large" className="refreshIcon" />
      ) : (
        <RefreshIcon onClick={onRefresh} fontSize="large" className="refreshIcon" />
      )}
      {!omitSwitch && (
        <Stack direction="row" alignItems="center">
          <Typography variant="body2">Org Resources</Typography>
          <Switch checked={isOrgView} onChange={onChange} />
        </Stack>
      )}
    </Stack>
  );
};

const useResourceCountController = (
  externalIsOrgView,
  externalOnControlChange,
  defaultIsOrgView = false,
) => {
  const [internalIsOrgView, setInternalIsOrgView] = useState(defaultIsOrgView);
  const isControlled = !!externalOnControlChange;
  const isOrgView = isControlled ? externalIsOrgView : internalIsOrgView;

  const onControlChange = () => {
    if (isControlled) {
      externalOnControlChange();
    } else {
      setInternalIsOrgView((prev) => !prev);
    }
  };

  return {
    isOrgView,
    isControlled,
    onControlChange,
  };
};

export const ResourceCount = (props: ResourceCountProps) => {
  const { showShortTitle, omitHeaderTextInfo, externalIsOrgView, externalOnControlChange } = props;

  const dispatch = useAppDispatch();
  const { isOrgView, isControlled, onControlChange } = useResourceCountController(
    externalIsOrgView,
    externalOnControlChange,
    false,
  );

  const by = isOrgView ? 'org' : 'user';
  const user = useCurrentUser();
  const labelViewType = user.isAdmin ? (isOrgView ? 'Org Resources' : 'My Resources') : ''; // No label for non-admin users since they only see their own data
  const org = useCurrentOrg();
  const isLoading = useIsLoading(GET_RESOURCE_COUNT_REQUEST);
  const { currentMode, ...summary } = useResourceCount(by);
  const loading = isLoading && currentMode === by;

  useEffect(() => {
    dispatch(fetchAccountSummaryByOrg(by));
  }, [by]);

  const onRefresh = () => {
    dispatch(fetchAccountSummaryByOrg(by, true));
  };

  const title = (() => {
    if (showShortTitle) return 'Resource Count';
    const prefix = by === 'user' ? user.full_name : org.name;
    return `${prefix}'s Resource Count`;
  })();

  return (
    <Stack>
      {omitHeaderTextInfo ? (
        <Stack className={props.className}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
            <Typography variant="subtitle1">{title}</Typography>
            <RightView
              onRefresh={onRefresh}
              admin={user.isAdmin}
              onChange={onControlChange}
              isLoading={loading}
              isOrgView={isOrgView}
              omitSwitch={isControlled}
            />
          </Stack>
        </Stack>
      ) : (
        <Stack className={props.className} borderBottom="1px solid var(--color-outline)">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid var(--color-outline)"
            padding="16px"
            gap="16px"
          >
            <KnowledgePanel
              alwaysShowUnderline
              knowledgeContent={
                <Typography sx={{ p: 2 }}>
                  Your resource usage in the Org. <br></br>Administrators can view Org resources
                  count.
                </Typography>
              }
            >
              <Typography variant="subtitle1">{title}</Typography>
            </KnowledgePanel>
            <RightView
              onRefresh={onRefresh}
              admin={user.isAdmin}
              onChange={onControlChange}
              isLoading={loading}
              isOrgView={isOrgView}
              omitSwitch={isControlled}
            />
          </Stack>

          {!omitHeaderTextInfo ? (
            <Stack direction="row" padding="16px" justifyContent="space-between">
              <Info
                loading={loading}
                value={FormatUtils.formatNamedNumber(summary.data_sources.total)}
                label="SOURCES"
              />
              <Info
                loading={loading}
                value={FormatUtils.formatNamedNumber(summary.data_sets.detected.total)}
                label="NEXSETS DETECTED BY NEXLA"
              />
              <Info
                loading={loading}
                value={FormatUtils.formatNamedNumber(summary.data_sets.derived.total)}
                label="NEXSETS FROM NEXSETS"
              />
              <Info
                loading={loading}
                value={FormatUtils.formatNamedNumber(summary.data_sinks.total)}
                label="DESTINATIONS"
              />
            </Stack>
          ) : null}
        </Stack>
      )}

      {props.detailed ? (
        <Stack direction="row" marginTop="16px" gap="10px" justifyContent="space-between">
          {/* Data Sources */}
          <div className={styles.resourceCardContainer}>
            <div className={styles.cardHeader}>
              <Typography className={styles.primaryTitle}>Data Sources</Typography>
              <span className={styles.secondaryTitle}>{labelViewType}</span>
            </div>

            <Stack className={styles.table}>
              {/* Table Header */}
              <Stack className={styles.tableHeader}>
                <span className={styles.tableHeaderTitle}>status</span>
                <span className={styles.tableHeaderTitle}>count</span>
              </Stack>

              {/* Active Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.ACTIVE} />
                </Stack>
                <Typography variant="body3">{summary?.data_sources?.active || 0}</Typography>
              </Stack>

              {/* Paused Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.PAUSED} />
                </Stack>
                <Typography variant="body3">{summary?.data_sources?.paused || 0}</Typography>
              </Stack>

              {/* Initialized Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.INIT} />
                </Stack>
                <Typography variant="body3">{summary?.data_sources?.initialized || 0}</Typography>
              </Stack>

              {/* Rate Limited Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.RATE_LIMITED} />
                </Stack>
                <Typography variant="body3">{summary?.data_sources?.rate_limited || 0}</Typography>
              </Stack>

              {/* Total Row */}
              <Stack className={styles.tableRow}>
                <Typography variant="caption">Total</Typography>
                <Typography variant="caption">{summary?.data_sources?.total || 0}</Typography>
              </Stack>
            </Stack>
          </div>

          {/* Data Sinks */}
          <div className={styles.resourceCardContainer}>
            <div className={styles.cardHeader}>
              <Typography className={styles.primaryTitle}>Data Sinks</Typography>
              <span className={styles.secondaryTitle}>{labelViewType}</span>
            </div>

            <Stack className={styles.table}>
              {/* Table Header */}
              <Stack className={styles.tableHeader}>
                <span className={styles.tableHeaderTitle}>status</span>
                <span className={styles.tableHeaderTitle}>count</span>
              </Stack>

              {/* Active Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.ACTIVE} />
                </Stack>
                <Typography variant="body3">{summary?.data_sinks?.active || 0}</Typography>
              </Stack>

              {/* Paused Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.PAUSED} />
                </Stack>
                <Typography variant="body3">{summary?.data_sinks?.paused || 0}</Typography>
              </Stack>

              {/* Initialized Row */}
              <Stack className={styles.tableRow}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <LightningStatus status={Constants.SourceStatus.INIT} />
                </Stack>
                <Typography variant="body3">{summary?.data_sinks?.initialized || 0}</Typography>
              </Stack>

              {/* Total Row */}
              <Stack className={styles.tableRow}>
                <Typography variant="caption">Total</Typography>
                <Typography variant="caption">{summary?.data_sinks?.total || 0}</Typography>
              </Stack>
            </Stack>
          </div>

          {/* Nexsets Table */}
          <div className={styles.resourceCardContainer}>
            <div className={styles.cardHeader}>
              <Typography className={styles.primaryTitle}>Nexsets</Typography>
              <span className={styles.secondaryTitle}>{labelViewType}</span>
            </div>

            <Stack className={styles.table}>
              {/* Table Header */}
              <Stack className={styles.tableHeader}>
                <span className={styles.tableHeaderTitle}>type</span>
                <span className={styles.tableHeaderTitle}>active</span>
                <span className={styles.tableHeaderTitle}>total</span>
              </Stack>

              {/* Detected Row */}
              <Stack className={styles.tableRow}>
                <Typography variant="body3">Detected</Typography>
                <Typography variant="body3">{summary?.data_sets?.detected?.active || 0}</Typography>
                <Typography variant="body3">{summary?.data_sets?.detected?.total || 0}</Typography>
              </Stack>

              {/* Derived Row */}
              <Stack className={styles.tableRow}>
                <Typography variant="body3">Derived</Typography>
                <Typography variant="body3">{summary?.data_sets?.derived?.active || 0}</Typography>
                <Typography variant="body3">{summary?.data_sets?.derived?.total || 0}</Typography>
              </Stack>

              {/* Total Row */}
              <Stack className={styles.tableRow}>
                <Typography variant="caption">Total</Typography>
                <Typography variant="caption">
                  {(summary?.data_sets?.detected?.active || 0) +
                    (summary?.data_sets?.derived?.active || 0)}
                </Typography>
                <Typography variant="caption">
                  {(summary?.data_sets?.detected?.total || 0) +
                    (summary?.data_sets?.derived?.total || 0)}
                </Typography>
              </Stack>
            </Stack>
          </div>
        </Stack>
      ) : null}
    </Stack>
  );
};
